import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import "./PosizioniNonCoerenti.css";
import RotateDevice from "../components/RotateDevice/RotateDevice";
import DatePickerModal from "../components/DatePickerModal/DatePickerModal";
import Lf23Loader from "../components/Lf23Loader";

const PosizioniNonCoerenti = () => {
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [dati, setDati] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [limit, setLimit] = useState(20);
    const [showInfo, setShowInfo] = useState(false);

    // UI State
    const [viewMode, setViewMode] = useState("table");
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [noteFeedback, setNoteFeedback] = useState(null);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreen]);
    const [showFilters, setShowFilters] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedRows, setExpandedRows] = useState(new Set());

    const [sortConfig, setSortConfig] = useState({ key: 'data rifornimento', direction: 'desc' });

    const handleSort = (key) => {
        setSortConfig(prev =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
    };

    // Stats globali
    const [stats, setStats] = useState({
        totalAnomalieEffettive: 0,
        totalImporto: 0,
        avgImporto: 0
    });

    const toggleRowExpansion = (idx) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(idx)) {
            newExpanded.delete(idx);
        } else {
            newExpanded.add(idx);
        }
        setExpandedRows(newExpanded);
    };

    // Helper function to get default date range (last 7 days)
    const getDefaultDateRange = () => {
        const today = new Date();
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);

        const formatDate = (date) => {
            return date.toISOString().split('T')[0]; // YYYY-MM-DD format
        };

        return {
            from: formatDate(sevenDaysAgo),
            to: formatDate(today)
        };
    };

    const defaultDates = getDefaultDateRange();

    // Filtri
    const [driver, setDriver] = useState("");
    const [tipoAnomalia, setTipoAnomalia] = useState("TUTTE");
    const [fromDate, setFromDate] = useState(defaultDates.from);
    const [toDate, setToDate] = useState(defaultDates.to);
    const [targa, setTarga] = useState("");
    const [costo, setCosto] = useState([
        { value: "0-20", label: "0-20€" },
        { value: "21-50", label: "21-50€" },
        { value: "50+", label: ">50€" }
    ]);
    const [anomaliaTransazione, setAnomaliaTransazione] = useState([
        { value: "DA VERIFICARE", label: "DA VERIFICARE" },
        { value: "UNDEFINED", label: "UNDEFINED" }
    ]);

    const [anomaliaAlimentazione, setAnomaliaAlimentazione] = useState("TUTTI");

    const costoOptions = [
        { value: "0-20", label: "0-20€" },
        { value: "21-50", label: "21-50€" },
        { value: "50+", label: ">50€" }
    ];

    const fetchStats = async () => {
        try {
            // Fetch tutte le righe per calcolare le statistiche globali
            const res = await axios.get(`${API_URL}/dkv/coerenza-posizioni`, {
                params: {
                    page: 1,
                    limit: 999999, // Prendi tutti i dati per le statistiche
                    fromDate: fromDate || undefined,
                    toDate: toDate || undefined,
                    targa,
                    driver,
                    tipoAnomalia,
                    costo: costo.map(c => c.value).join(","),
                    anomaliaTransazione: anomaliaTransazione.map(a => a.value).join(","),
                    anomaliaAlimentazione
                }
            });

            const allData = res.data.data || [];

            // Calcola statistiche globali
            const totalImporto = allData.reduce((sum, row) => sum + (row.importo || 0), 0);
            const totalAnomalieEffettive = allData.filter(row => {
                const statoAnomalia = row["Anomalia alimentazione"] === "SI"
                    ? "DA VERIFICARE"
                    : (row["Anomalia transazione"] || "").trim().toUpperCase();
                return ["DA VERIFICARE", "UNDEFINED"].includes(statoAnomalia);
            }).length;
            const avgImporto = allData.length > 0 ? totalImporto / allData.length : 0;

            setStats({
                totalAnomalieEffettive,
                totalImporto,
                avgImporto
            });
        } catch (err) {
            console.error("Errore nel caricamento statistiche:", err);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/dkv/coerenza-posizioni`, {
                params: {
                    page,
                    limit,
                    fromDate: fromDate || undefined,
                    toDate: toDate || undefined,
                    targa,
                    driver,
                    tipoAnomalia,
                    costo: costo.map(c => c.value).join(","),
                    anomaliaTransazione: anomaliaTransazione.map(a => a.value).join(","),
                    anomaliaAlimentazione,
                    sortBy: sortConfig.key || undefined,
                    sortDir: sortConfig.direction || undefined
                }
            });
            setDati(res.data.data);
            setTotal(res.data.total);
        } catch (err) {
            console.error("Errore nel caricamento dati:", err);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
        fetchStats(); // Fetch anche le statistiche
    }, [page, limit, sortConfig]);

    useEffect(() => {
        setPage(1);
    }, [sortConfig]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
            alert("La data iniziale non può essere successiva alla data finale.");
            return;
        }
        setPage(1);
        fetchData();
        fetchStats();
    };

    const handleReset = () => {
        const defaultDates = getDefaultDateRange();
        setFromDate(defaultDates.from);
        setToDate(defaultDates.to);
        setTarga("");
        setDriver("");
        setTipoAnomalia("TUTTE");
        setCosto(costoOptions);
        setAnomaliaTransazione([
            { value: "DA VERIFICARE", label: "DA VERIFICARE" },
            { value: "UNDEFINED", label: "UNDEFINED" }
        ]);
        setAnomaliaAlimentazione("TUTTI");
        setSearchTerm("");
        setPage(1);
        fetchData();
        fetchStats();
    };

    const filteredDati = dati.filter(row => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            row.targa?.toLowerCase().includes(search) ||
            row.driver?.toLowerCase().includes(search) ||
            row.stato_anomalia?.toLowerCase().includes(search) ||
            row.tipo_anomalia?.toLowerCase().includes(search)
        );
    });

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="posizioni-container">
            <header className="page-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate('/produttivita')}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        Indietro
                    </button>
                    <div className="header-title">
                        <div className="header-title-row">
                            <h2>Transazioni Non Coerenti</h2>
                            <button
                                className="info-button-modern"
                                onClick={() => setShowInfo(!showInfo)}
                                title="Informazioni"
                            >
                                <span className="info-icon-text">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle">
                            Monitoraggio anomalie nelle transazioni di rifornimento
                        </p>
                    </div>
                </div>
            </header>

            {/* Modal Informazioni */}
            {showInfo && (
                <div className="modal-overlay" onClick={() => setShowInfo(false)}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                                    <path d="M10 8h6"></path>
                                    <path d="M10 12h6"></path>
                                    <path d="M10 16h4"></path>
                                </svg>
                            </div>
                            <h3>Informazioni sulla pagina</h3>
                            <button className="modal-close" onClick={() => setShowInfo(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    In questa sezione vengono visualizzate tutte le <strong>transazioni di rifornimento</strong>, con particolare
                                    attenzione a quelle che presentano anomalie legate alla posizione del veicolo o al tipo di alimentazione rilevato.
                                </p>
                            </div>
                            <div className="modal-section">
                                <h4>Stati Anomalia</h4>
                                <p>Per ogni transazione viene assegnato uno <strong>Stato Anomalia</strong> in base alle seguenti condizioni:</p>
                                <ul>
                                    <li><strong>DA VERIFICARE</strong> - Incoerenza tra alimentazione veicolo e carburante erogato</li>
                                    <li><strong>UNDEFINED</strong> - Nessun movimento del veicolo nei 30 minuti precedenti</li>
                                    <li><strong>OK</strong> - Velocità necessaria ≤ 70 km/h</li>
                                    <li><strong>ACCETTABILE</strong> - Velocità tra 70 e 120 km/h</li>
                                    <li><strong>DA VERIFICARE</strong> - Velocità ≥ 120 km/h</li>
                                </ul>
                            </div>
                            <div className="modal-section">
                                <h4>Dettaglio Anomalia</h4>
                                <p>Per ogni riga è disponibile un campo <strong>Dettaglio Anomalia</strong> che specifica:</p>
                                <ul>
                                    <li><strong>Tipo</strong> - Indica se l'anomalia riguarda la Posizione o la Merce (carburante incoerente)</li>
                                    <li><strong>Dettaglio</strong> - Spiega la natura dell'anomalia (es. "Posizione non coerente" o "Tipo alimentazione non coerente")</li>
                                </ul>
                                <p className="modal-tip">
                                    💡 Utilizza i filtri per raffinare la ricerca in base a stato anomalia, tipo, periodo e altri criteri specifici.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="content-area">
                {/* Top Controls */}
                <div className="top-controls">
                    <div className="controls-left">
                        <button
                            className={`toggle-filters-btn ${showFilters ? 'active' : ''}`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                            </svg>
                            {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
                        </button>

                        <div className="search-bar-compact">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input
                                type="text"
                                placeholder="Cerca..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="controls-right">
                        <div className="view-toggle-compact">
                            <button
                                className={`view-btn-compact ${viewMode === 'table' ? 'active' : ''}`}
                                onClick={() => setViewMode('table')}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 9h18M3 15h18M9 3v18"></path>
                                </svg>
                                Tabella
                            </button>
                            <button
                                className={`view-btn-compact ${viewMode === 'cards' ? 'active' : ''}`}
                                onClick={() => setViewMode('cards')}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="14" width="7" height="7"></rect>
                                    <rect x="3" y="14" width="7" height="7"></rect>
                                </svg>
                                Cards
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="filters-panel">
                        <form className="filters-form" onSubmit={handleSubmit}>
                            <div className="filters-grid">
                                <div className="filter-item">
                                    <label>Dal</label>
                                    <DatePickerModal
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        max={toDate || undefined}
                                        label="Dal"
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Al</label>
                                    <DatePickerModal
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        min={fromDate || undefined}
                                        label="Al"
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Targa</label>
                                    <input
                                        type="text"
                                        placeholder="Targa..."
                                        value={targa}
                                        onChange={(e) => setTarga(e.target.value.trim())}
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Driver</label>
                                    <input
                                        type="text"
                                        placeholder="Driver..."
                                        value={driver}
                                        onChange={(e) => setDriver(e.target.value)}
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Tipo Anomalia</label>
                                    <select value={tipoAnomalia} onChange={(e) => setTipoAnomalia(e.target.value)}>
                                        <option value="TUTTE">Tutte</option>
                                        <option value="MERCE">Merce</option>
                                        <option value="POSIZIONI">Posizione</option>
                                    </select>
                                </div>

                                <div className="filter-item">
                                    <label>Stato Anomalia</label>
                                    <Select
                                        isMulti
                                        classNamePrefix="rs"
                                        options={[
                                            { value: "OK", label: "OK" },
                                            { value: "ACCETTABILE", label: "ACCETTABILE" },
                                            { value: "DA VERIFICARE", label: "DA VERIFICARE" },
                                            { value: "UNDEFINED", label: "UNDEFINED" }
                                        ]}
                                        value={anomaliaTransazione}
                                        onChange={setAnomaliaTransazione}
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Importo</label>
                                    <Select
                                        isMulti
                                        classNamePrefix="rs"
                                        options={costoOptions}
                                        value={costo}
                                        onChange={setCosto}
                                    />
                                </div>

                                <div className="filter-item filter-actions">
                                    <label>&nbsp;</label>
                                    <div className="filter-buttons">
                                        <button type="submit" className="btn-apply-compact">Filtra</button>
                                        <button type="button" className="btn-reset-compact" onClick={handleReset}>Reset filtro</button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {/* Stats Cards */}
                <div className="stats-grid-compact">
                    <div className="stat-card-compact stat-primary">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Anomalie</p>
                            <p className="stat-value-compact">{stats.totalAnomalieEffettive}</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-success">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="1" x2="12" y2="23"></line>
                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Importo Totale</p>
                            <p className="stat-value-compact">{stats.totalImporto.toFixed(2)} €</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-warning">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="20" x2="18" y2="10"></line>
                                <line x1="12" y1="20" x2="12" y2="4"></line>
                                <line x1="6" y1="20" x2="6" y2="14"></line>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Media importo</p>
                            <p className="stat-value-compact">{stats.avgImporto.toFixed(2)} €</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-info">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Periodo</p>
                            <p className="stat-value-compact">
                                {fromDate && toDate
                                    ? `${new Date(fromDate).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })} - ${new Date(toDate).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}`
                                    : 'Non specificato'}
                            </p>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <Lf23Loader />
                ) : (
                    <>
                        {/* Table View */}
                        {viewMode === 'table' && (
                            <>
                            <div className="fullscreen-btn-row">
                                <button
                                    className="fullscreen-btn-compact"
                                    onClick={() => setIsFullscreen(!isFullscreen)}
                                    title={isFullscreen ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreen ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                            <div className={`table-container-compact${isFullscreen ? ' fullscreen-table' : ''}`}>
                                <RotateDevice active={isFullscreen} />
                                {isFullscreen && (
                                    <div className="fullscreen-exit-bar">
                                        <button
                                            className="fullscreen-btn-compact"
                                            onClick={() => setIsFullscreen(false)}
                                            title="Esci da tutto schermo"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </svg>
                                        </button>
                                    </div>
                                )}
                                <div className="table-scroll-area">
                                <table className="modern-table-compact">
                                    <thead>
                                    <tr>
                                        <th style={{width: '50px', textAlign: 'center'}}></th>
                                        {[
                                            { label: 'Data', key: 'data rifornimento' },
                                            { label: 'Targa', key: 'targa' },
                                            { label: 'Stato Anomalia', key: 'Anomalia transazione' },
                                            { label: 'Importo', key: 'importo' },
                                            { label: 'Velocità / Gap', key: 'velocità ipotetica necessaria' },
                                            { label: 'Gestito', key: 'gestito', style: { width: '80px', textAlign: 'center' } },
                                        ].map(({ label, key, style }) => (
                                            <th key={key} className="posizioni-th-sortable" onClick={() => handleSort(key)} style={style || {}}>
                                                {label}
                                                <span className="posizioni-sort-icon">
                                                    {sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredDati.map((row, idx) => {
                                        const isExpanded = expandedRows.has(idx);
                                        const statoAnomalia = row["Anomalia alimentazione"] === "SI"
                                            ? "DA VERIFICARE"
                                            : (row["Anomalia transazione"] || "").trim().toUpperCase();

                                        const isAnomalia = ["DA VERIFICARE", "UNDEFINED"].includes(statoAnomalia);

                                        return (
                                            <React.Fragment key={idx}>
                                                <tr className="table-row-compact" onClick={() => toggleRowExpansion(idx)}>
                                                    <td className="expand-cell-compact">
                                                        <button className="expand-btn-compact" onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleRowExpansion(idx);
                                                        }}>
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{
                                                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                                transition: 'transform 0.2s'
                                                            }}>
                                                                <polyline points="6 9 12 15 18 9"></polyline>
                                                            </svg>
                                                        </button>
                                                    </td>
                                                    <td style={{ fontWeight: '500' }}>{new Date(row["data rifornimento"]).toLocaleString("it-IT")}</td>
                                                    <td>
                                                        <span className="badge-primary-compact">{row.targa}</span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge-status-compact badge-${
                                                            statoAnomalia === "OK" ? "ok" :
                                                            statoAnomalia === "ACCETTABILE" ? "accettabile" :
                                                            statoAnomalia === "UNDEFINED" ? "undefined" :
                                                            "verificare"
                                                        }`}>
                                                            {statoAnomalia}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="badge-euro-compact">
                                                            {row.importo != null ? `${parseFloat(row.importo).toFixed(2)} €` : "-"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {row["velocità ipotetica necessaria"] != null ? (
                                                            <span className="badge-speed-compact">
                                                                {parseFloat(row["velocità ipotetica necessaria"]).toFixed(2)} km/h
                                                            </span>
                                                        ) : "-"}
                                                        {row["distanza tra le due posizioni"] != null && (
                                                            <span className="badge-distance-compact" style={{marginLeft: '6px'}}>
                                                                {parseFloat(row["distanza tra le due posizioni"]).toFixed(2)} km
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{textAlign: 'center'}} onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            className="checkbox-compact"
                                                            checked={row.gestito}
                                                            onChange={async () => {
                                                                const nuovoStato = !row.gestito;
                                                                try {
                                                                    await axios.post(`${API_URL}/dkv/gestito`, {
                                                                        data: row["data rifornimento"],
                                                                        targa: row.targa,
                                                                        gestito: nuovoStato,
                                                                        note: row.note
                                                                    });

                                                                    setDati(prev => {
                                                                        const nuovo = [...prev];
                                                                        nuovo[idx] = { ...nuovo[idx], gestito: nuovoStato };
                                                                        return nuovo;
                                                                    });
                                                                } catch (err) {
                                                                    console.error("Errore aggiornamento gestito:", err);
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="expanded-row-compact">
                                                        <td colSpan="7">
                                                            <div className="expanded-content-compact">
                                                                {/* Row principale con 3 sezioni affiancate */}
                                                                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px'}}>
                                                                    {/* Dettaglio Transazione */}
                                                                    <div style={{background: '#F8FAFC', padding: '12px', borderRadius: '8px', border: '1px solid #E2E8F0'}}>
                                                                        <h4 style={{fontSize: '13px', fontWeight: '700', color: '#2E6DB1', margin: '0 0 10px 0', textTransform: 'uppercase'}}>
                                                                            Transazione
                                                                        </h4>
                                                                        <div className="expanded-grid-compact">
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Data</span>
                                                                                <span className="expanded-value-compact">
                                                                                    {new Date(row["data rifornimento"]).toLocaleString()}
                                                                                </span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Importo</span>
                                                                                <span className="expanded-value-compact" style={{color: '#10B981', fontWeight: '700'}}>
                                                                                    {row.importo != null ? `${parseFloat(row.importo).toFixed(2)} €` : "-"}
                                                                                </span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Merce</span>
                                                                                <span className="expanded-value-compact">{row.merce || "-"}</span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Stazione</span>
                                                                                <span className="expanded-value-compact">{row["stazione rifornimento"] || "-"}</span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Marchio</span>
                                                                                <span className="expanded-value-compact">{row["tipo stazione"] || "-"}</span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Referente</span>
                                                                                <span className="expanded-value-compact">{row.referente || "-"}</span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Indirizzo</span>
                                                                                <span className="expanded-value-compact">{row.posizione_stazione || "-"}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Dettaglio Veicolo */}
                                                                    <div style={{background: '#F8FAFC', padding: '12px', borderRadius: '8px', border: '1px solid #E2E8F0'}}>
                                                                        <h4 style={{fontSize: '13px', fontWeight: '700', color: '#2E6DB1', margin: '0 0 10px 0', textTransform: 'uppercase'}}>
                                                                            Veicolo
                                                                        </h4>
                                                                        <div className="expanded-grid-compact">
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Data Pos.</span>
                                                                                <span className="expanded-value-compact">
                                                                                    {row["pos_time"] ? new Date(row["pos_time"]).toLocaleString() : "-"}
                                                                                </span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Driver</span>
                                                                                <span className="expanded-value-compact">{row["drivername"] || "-"}</span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Alimentazione</span>
                                                                                <span className="expanded-value-compact">{row["alimentazione"] || "-"}</span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Coordinate</span>
                                                                                <span className="expanded-value-compact" style={{fontSize: '11px'}}>
                                                                                    {row["pos_lat"] != null && row["pos_long"] != null &&
                                                                                    !isNaN(parseFloat(row["pos_lat"])) && !isNaN(parseFloat(row["pos_long"]))
                                                                                        ? `${parseFloat(row["pos_lat"]).toFixed(6)}, ${parseFloat(row["pos_long"]).toFixed(6)}`
                                                                                        : "-"}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Gap e Velocità */}
                                                                    <div style={{background: '#F8FAFC', padding: '12px', borderRadius: '8px', border: '1px solid #E2E8F0'}}>
                                                                        <h4 style={{fontSize: '13px', fontWeight: '700', color: '#2E6DB1', margin: '0 0 10px 0', textTransform: 'uppercase'}}>
                                                                            Gap & Velocità
                                                                        </h4>
                                                                        <div className="expanded-grid-compact">
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Distanza</span>
                                                                                <span className="expanded-value-compact">
                                                                                    {row["distanza tra le due posizioni"] != null
                                                                                        ? `${parseFloat(row["distanza tra le due posizioni"]).toFixed(2)} Km`
                                                                                        : "-"}
                                                                                </span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Tempo</span>
                                                                                <span className="expanded-value-compact">
                                                                                    {(() => {
                                                                                        const raw = row["tempo tra le due posizioni"];
                                                                                        if (!raw || raw === "NaT" || raw === "00:00:00") return "-";

                                                                                        const parts = raw.split(":");
                                                                                        if (parts.length !== 3) return raw;

                                                                                        const [h, m, s] = parts.map(p => parseInt(p, 10));
                                                                                        const d = Math.floor(h / 24);
                                                                                        const hh = h % 24;

                                                                                        const result = [];
                                                                                        if (d) result.push(`${d} giorno${d > 1 ? 'i' : ''}`);
                                                                                        if (hh) result.push(`${hh}h`);
                                                                                        if (m) result.push(`${m}m`);
                                                                                        if (s) result.push(`${s}s`);

                                                                                        return result.length ? result.join(" ") : "0s";
                                                                                    })()}
                                                                                </span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Velocità</span>
                                                                                <span className="expanded-value-compact" style={{color: '#EF4444', fontWeight: '700'}}>
                                                                                    {row["velocità ipotetica necessaria"] != null
                                                                                        ? `${parseFloat(row["velocità ipotetica necessaria"]).toFixed(2)} km/h`
                                                                                        : "-"}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Dettaglio Anomalia e Note */}
                                                                <div className="dettaglio-note-grid" style={{display: 'grid', gridTemplateColumns: !["OK", "ACCETTABILE"].includes(statoAnomalia) ? '1fr 2fr' : '1fr', gap: '16px'}}>
                                                                    {!["OK", "ACCETTABILE"].includes(statoAnomalia) && (
                                                                        <div className="expanded-item-compact">
                                                                            <span className="expanded-label-compact">Dettaglio Anomalia</span>
                                                                            <span className="expanded-value-compact">
                                                                                <strong>Tipo:</strong> {row["Anomalia alimentazione"] === "SI" ? "Merce" : "Posizione"}
                                                                                <br/>
                                                                                <strong>Dettaglio:</strong> {row["Anomalia alimentazione"] === "SI"
                                                                                    ? `Alimentazione non coerente: ${row["Merce prevista su erogata"]}`
                                                                                    : "Posizione non coerente"}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    <div className="expanded-item-compact">
                                                                        <span className="expanded-label-compact">Note</span>
                                                                        <div className="nota-wrapper" style={{ display: 'flex', gap: '10px', width: '100%', alignItems: 'flex-start' }}>
                                                                            <textarea className="nota-textarea"
                                                                                defaultValue={row.note || ""}
                                                                                id={`nota-pnc-${row.targa}-${row["data rifornimento"]}`}
                                                                                style={{
                                                                                    flex: 1,
                                                                                    minHeight: '60px',
                                                                                    padding: '8px',
                                                                                    border: '1px solid #E2E8F0',
                                                                                    borderRadius: '6px',
                                                                                    fontSize: '13px',
                                                                                    fontFamily: 'inherit',
                                                                                    resize: 'vertical',
                                                                                    boxSizing: 'border-box'
                                                                                }}
                                                                                placeholder="Aggiungi nota..."
                                                                            />
                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                                                                                <button
                                                                                    onClick={async () => {
                                                                                        const textarea = document.getElementById(
                                                                                            `nota-pnc-${row.targa}-${row["data rifornimento"]}`
                                                                                        );
                                                                                        const nuovaNota = textarea.value;
                                                                                        if (nuovaNota === row.note) return;

                                                                                        try {
                                                                                            await axios.post(`${API_URL}/dkv/gestito`, {
                                                                                                data: row["data rifornimento"],
                                                                                                targa: row.targa,
                                                                                                gestito: row.gestito,
                                                                                                note: nuovaNota
                                                                                            });

                                                                                            setDati(prev => {
                                                                                                const nuovo = [...prev];
                                                                                                nuovo[idx] = { ...nuovo[idx], note: nuovaNota };
                                                                                                return nuovo;
                                                                                            });
                                                                                            const key = `${row.targa}-${row["data rifornimento"]}`;
                                                                                            setNoteFeedback({ key, type: 'success' });
                                                                                            setTimeout(() => setNoteFeedback(prev => prev?.key === key ? null : prev), 3000);
                                                                                        } catch (err) {
                                                                                            console.error("Errore aggiornamento nota:", err);
                                                                                            const key = `${row.targa}-${row["data rifornimento"]}`;
                                                                                            setNoteFeedback({ key, type: 'error' });
                                                                                            setTimeout(() => setNoteFeedback(prev => prev?.key === key ? null : prev), 3000);
                                                                                        }
                                                                                    }}
                                                                                    style={{
                                                                                        padding: '6px 16px',
                                                                                        backgroundColor: '#3B82F6',
                                                                                        color: 'white',
                                                                                        border: 'none',
                                                                                        borderRadius: '6px',
                                                                                        fontSize: '13px',
                                                                                        cursor: 'pointer',
                                                                                        fontWeight: '500'
                                                                                    }}
                                                                                >
                                                                                    Salva Nota
                                                                                </button>
                                                                                {noteFeedback?.key === `${row.targa}-${row["data rifornimento"]}` && (
                                                                                    <span style={{
                                                                                        fontSize: '13px',
                                                                                        fontWeight: '600',
                                                                                        padding: '4px 12px',
                                                                                        borderRadius: '6px',
                                                                                        background: noteFeedback.type === 'success' ? '#22C55E' : '#EF4444',
                                                                                        color: '#FFFFFF'
                                                                                    }}>
                                                                                        {noteFeedback.type === 'success' ? '✓ Nota salvata!' : '✗ Errore nel salvataggio!'}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    </tbody>
                                </table>
                                </div>

                                {/* Paginazione dentro table-container per fullscreen */}
                                <div className="pagination-compact">
                                    <button
                                        className="pagination-btn-compact"
                                        disabled={page === 1}
                                        onClick={() => setPage(page - 1)}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M15 18l-6-6 6-6" />
                                        </svg>
                                        Prec
                                    </button>
                                    <div className="pagination-info-compact">
                                        <span className="pagination-text-compact">
                                            Pag <strong>{page}</strong> di <strong>{totalPages || 1}</strong>
                                        </span>
                                        <span className="pagination-total-compact">({total} risultati totali)</span>
                                    </div>
                                    <button
                                        className="pagination-btn-compact"
                                        disabled={page === totalPages || totalPages === 0}
                                        onClick={() => setPage(page + 1)}
                                    >
                                        Succ
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M9 18l6-6-6-6" />
                                        </svg>
                                    </button>
                                    <div className="items-per-page-compact">
                                        <span className="items-label-compact">Elementi per pagina:</span>
                                        <div className="items-buttons-compact">
                                            {[10, 20, 50, 100].map((num) => (
                                                <button
                                                    key={num}
                                                    className={`items-btn-compact ${limit === num ? "active" : ""}`}
                                                    onClick={() => {
                                                        setLimit(num);
                                                        setPage(1);
                                                    }}
                                                >
                                                    {num}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </>
                        )}

                        {/* Cards View */}
                        {viewMode === 'cards' && (
                            <div className="cards-grid-compact">
                                {filteredDati.map((row, idx) => {
                                    const statoAnomalia = row["Anomalia alimentazione"] === "SI"
                                        ? "DA VERIFICARE"
                                        : (row["Anomalia transazione"] || "").trim().toUpperCase();

                                    const isAnomalia = ["DA VERIFICARE", "UNDEFINED"].includes(statoAnomalia);

                                    return (
                                        <div key={idx} className="data-card-compact">
                                            <div className="card-header-compact">
                                                <div className="card-title-row-compact">
                                                    <span className="badge-primary-compact">{row.targa}</span>
                                                    <span className={`badge-status-compact badge-${
                                                        statoAnomalia === "OK" ? "ok" :
                                                        statoAnomalia === "ACCETTABILE" ? "accettabile" :
                                                        statoAnomalia === "UNDEFINED" ? "undefined" :
                                                        "verificare"
                                                    }`}>
                                                        {statoAnomalia}
                                                    </span>
                                                </div>
                                                <div className="card-meta-compact">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="12" cy="12" r="10"></circle>
                                                        <polyline points="12 6 12 12 16 14"></polyline>
                                                    </svg>
                                                    {new Date(row["data rifornimento"]).toLocaleString()}
                                                </div>
                                            </div>

                                            <div className="card-body-compact">
                                                {/* Sezione Transazione */}
                                                <div style={{marginBottom: '12px'}}>
                                                    <p style={{fontSize: '11px', fontWeight: '600', color: '#64748B', margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                                                        Transazione
                                                    </p>
                                                    <div style={{fontSize: '13px', color: '#475569', lineHeight: '1.8'}}>
                                                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                                                            <strong>Importo:</strong>
                                                            <span style={{color: '#10B981', fontWeight: '700'}}>
                                                                {row.importo != null ? `${parseFloat(row.importo).toFixed(2)} €` : "-"}
                                                            </span>
                                                        </div>
                                                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                                                            <strong>Merce:</strong>
                                                            <span>{row.merce || "-"}</span>
                                                        </div>
                                                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                                                            <strong>Stazione:</strong>
                                                            <span>{row["stazione rifornimento"] || "-"}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="card-divider-compact"></div>

                                                {/* Sezione Veicolo */}
                                                <div style={{marginBottom: '12px'}}>
                                                    <p style={{fontSize: '11px', fontWeight: '600', color: '#64748B', margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                                                        Veicolo
                                                    </p>
                                                    <div style={{fontSize: '13px', color: '#475569', lineHeight: '1.8'}}>
                                                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                                                            <strong>Driver:</strong>
                                                            <span>{row["drivername"] || "-"}</span>
                                                        </div>
                                                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                                                            <strong>Alimentazione:</strong>
                                                            <span>{row["alimentazione"] || "-"}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="card-divider-compact"></div>

                                                {/* Sezione Gap e Velocità */}
                                                <div style={{marginBottom: '12px'}}>
                                                    <p style={{fontSize: '11px', fontWeight: '600', color: '#64748B', margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                                                        Gap & Velocità
                                                    </p>
                                                    <div style={{fontSize: '13px', color: '#475569', lineHeight: '1.8'}}>
                                                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                                                            <strong>Distanza:</strong>
                                                            <span>
                                                                {row["distanza tra le due posizioni"] != null
                                                                    ? `${parseFloat(row["distanza tra le due posizioni"]).toFixed(2)} km`
                                                                    : "-"}
                                                            </span>
                                                        </div>
                                                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                                                            <strong>Tempo:</strong>
                                                            <span>
                                                                {(() => {
                                                                    const raw = row["tempo tra le due posizioni"];
                                                                    if (!raw || raw === "NaT" || raw === "00:00:00") return "-";

                                                                    const parts = raw.split(":");
                                                                    if (parts.length !== 3) return raw;

                                                                    const [h, m, s] = parts.map(p => parseInt(p, 10));
                                                                    const d = Math.floor(h / 24);
                                                                    const hh = h % 24;

                                                                    const result = [];
                                                                    if (d) result.push(`${d}g`);
                                                                    if (hh) result.push(`${hh}h`);
                                                                    if (m) result.push(`${m}m`);

                                                                    return result.length ? result.join(" ") : "0m";
                                                                })()}
                                                            </span>
                                                        </div>
                                                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                                                            <strong>Velocità:</strong>
                                                            <span style={{color: '#EF4444', fontWeight: '700'}}>
                                                                {row["velocità ipotetica necessaria"] != null
                                                                    ? `${parseFloat(row["velocità ipotetica necessaria"]).toFixed(2)} km/h`
                                                                    : "-"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {!["OK", "ACCETTABILE"].includes(statoAnomalia) && (
                                                    <>
                                                        <div className="card-divider-compact"></div>
                                                        <div style={{
                                                            background: '#FEF2F2',
                                                            border: '1px solid #FEE2E2',
                                                            borderRadius: '6px',
                                                            padding: '10px',
                                                            fontSize: '12px',
                                                            color: '#991B1B'
                                                        }}>
                                                            <div style={{fontWeight: '600', marginBottom: '4px'}}>
                                                                🚨 Anomalia Rilevata
                                                            </div>
                                                            <div style={{lineHeight: '1.6'}}>
                                                                <strong>Tipo:</strong> {row["Anomalia alimentazione"] === "SI" ? "Merce" : "Posizione"}
                                                                <br/>
                                                                <strong>Dettaglio:</strong> {
                                                                    row["Anomalia alimentazione"] === "SI"
                                                                        ? `${row["Merce prevista su erogata"]}`
                                                                        : "Posizione non coerente"
                                                                }
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            <div className="card-footer-compact">
                                                <div style={{ marginBottom: '10px' }}>
                                                    <p style={{fontSize: '11px', fontWeight: '600', color: '#64748B', margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                                                        Note
                                                    </p>
                                                    <div className="nota-wrapper" style={{ display: 'flex', gap: '10px', width: '100%', alignItems: 'flex-start' }}>
                                                        <textarea className="nota-textarea"
                                                            defaultValue={row.note || ""}
                                                            id={`nota-pnc-card-${row.targa}-${row["data rifornimento"]}`}
                                                            style={{
                                                                flex: 1,
                                                                minHeight: '60px',
                                                                padding: '8px',
                                                                border: '1px solid #E2E8F0',
                                                                borderRadius: '6px',
                                                                fontSize: '13px',
                                                                fontFamily: 'inherit',
                                                                resize: 'vertical',
                                                                boxSizing: 'border-box'
                                                            }}
                                                            placeholder="Aggiungi nota..."
                                                        />
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                                                            <button
                                                                onClick={async () => {
                                                                    const textarea = document.getElementById(
                                                                        `nota-pnc-card-${row.targa}-${row["data rifornimento"]}`
                                                                    );
                                                                    const nuovaNota = textarea.value;
                                                                    if (nuovaNota === row.note) return;

                                                                    try {
                                                                        await axios.post(`${API_URL}/dkv/gestito`, {
                                                                            data: row["data rifornimento"],
                                                                            targa: row.targa,
                                                                            gestito: row.gestito,
                                                                            note: nuovaNota
                                                                        });

                                                                        setDati(prev => {
                                                                            const nuovo = [...prev];
                                                                            nuovo[idx] = { ...nuovo[idx], note: nuovaNota };
                                                                            return nuovo;
                                                                        });
                                                                        const key = `card-${row.targa}-${row["data rifornimento"]}`;
                                                                        setNoteFeedback({ key, type: 'success' });
                                                                        setTimeout(() => setNoteFeedback(prev => prev?.key === key ? null : prev), 3000);
                                                                    } catch (err) {
                                                                        console.error("Errore aggiornamento nota:", err);
                                                                        const key = `card-${row.targa}-${row["data rifornimento"]}`;
                                                                        setNoteFeedback({ key, type: 'error' });
                                                                        setTimeout(() => setNoteFeedback(prev => prev?.key === key ? null : prev), 3000);
                                                                    }
                                                                }}
                                                                style={{
                                                                    padding: '6px 16px',
                                                                    backgroundColor: '#3B82F6',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '6px',
                                                                    fontSize: '13px',
                                                                    cursor: 'pointer',
                                                                    fontWeight: '500'
                                                                }}
                                                            >
                                                                Salva Nota
                                                            </button>
                                                            {noteFeedback?.key === `card-${row.targa}-${row["data rifornimento"]}` && (
                                                                <span style={{
                                                                    fontSize: '13px',
                                                                    fontWeight: '600',
                                                                    padding: '4px 12px',
                                                                    borderRadius: '6px',
                                                                    background: noteFeedback.type === 'success' ? '#22C55E' : '#EF4444',
                                                                    color: '#FFFFFF'
                                                                }}>
                                                                    {noteFeedback.type === 'success' ? '✓ Nota salvata!' : '✗ Errore nel salvataggio!'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="card-divider-compact"></div>

                                                <label className="checkbox-label-compact">
                                                    <input
                                                        type="checkbox"
                                                        className="checkbox-compact"
                                                        checked={row.gestito}
                                                        onChange={async () => {
                                                            const nuovoStato = !row.gestito;
                                                            try {
                                                                await axios.post(`${API_URL}/dkv/gestito`, {
                                                                    data: row["data rifornimento"],
                                                                    targa: row.targa,
                                                                    gestito: nuovoStato,
                                                                    note: row.note
                                                                });

                                                                setDati(prev => {
                                                                    const nuovo = [...prev];
                                                                    nuovo[idx] = { ...nuovo[idx], gestito: nuovoStato };
                                                                    return nuovo;
                                                                });
                                                            } catch (err) {
                                                                console.error("Errore aggiornamento gestito:", err);
                                                            }
                                                        }}
                                                    />
                                                    Gestito
                                                </label>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                    </>
                )}
            </div>

        </div>
    );
};

export default PosizioniNonCoerenti;
